import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import StatusChip from '../components/shared/StatusChip';
import ConfidenceBadge from '../components/shared/ConfidenceBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import {
  Activity, Pill, AlertCircle, Calendar, FileText, Beaker,
  HelpCircle, ShieldCheck, Clock, Loader2,
  ClipboardList, Shield, Users, ChevronRight, ChevronDown, ChevronLeft, History, Check, X,
  Stethoscope, FolderOpen, Send, LayoutGrid, Search, Brain, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import QualityStatusPill from '../components/encounter/QualityStatusPill';
import { QualityChipStrip } from '../components/quality/QualityChipStrip';
import { QualityChipStripDrawer } from '../components/quality/QualityChipStripDrawer';

// Extracted components (P0, P1, P2)
import OverviewContent from '../components/patient_chart/OverviewContent';
import NotesContent from '../components/patient_chart/NotesContent';
import MedsContent from '../components/patient_chart/MedsContent';
import LabDashboard from './LabDashboard';

// ============================================================
// CHART SECTIONS CONFIG
// ============================================================
const SECTIONS = [
  { id: 'overview',   label: 'Overview',          icon: LayoutGrid,  group: 'Clinical' },
  { id: 'problems',   label: 'Problems',           icon: Activity,    group: 'Clinical' },
  { id: 'notes',      label: 'Notes',              icon: FileText,    group: 'Clinical' },
  { id: 'meds',       label: 'Medications',        icon: Pill,        group: 'Clinical' },
  { id: 'preventive', label: 'Preventive Care',    icon: Shield,      group: 'Clinical' },
  { id: 'family',     label: 'Family History',     icon: Users,       group: 'History' },
  { id: 'social',     label: 'Social History',     icon: Activity,    group: 'History' },
  { id: 'labs',       label: 'Lab Results',        icon: Beaker,      group: 'History' },
  { id: 'referrals',  label: 'Referrals',          icon: Send,        group: 'History' },
  { id: 'documents',  label: 'Documents',          icon: FolderOpen,  group: 'History' },
  { id: 'tasks',      label: 'Tasks',              icon: ClipboardList, group: 'Admin' },
  { id: 'deferred',   label: 'Deferred Updates',   icon: History,     group: 'Admin' },
  { id: 'care_team',  label: 'Care Team',          icon: Users,       group: 'Admin' },
];

// ============================================================
// CONTENT VIEWS (OverviewContent, NotesContent, MedsContent extracted to components/patient_chart/)
// ============================================================


function ProblemsContent({ problemList, patientId }) {
  const { SmartProblemListEditor } = require('../components/patient_chart/SmartProblemListEditor');
  return <SmartProblemListEditor patientId={patientId} problemList={problemList} />;
}

// NotesContent and MedsContent extracted to components/patient_chart/

function AllergiesContent({ allergies }) {
  return (
    <Card>
      <CardContent className="p-0">
        {(allergies || []).map((a, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 border-b last:border-0">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-sm font-medium">{a.name}</p>
                {a.reaction && <p className="text-xs text-muted-foreground">Reaction: {a.reaction}</p>}
              </div>
            </div>
            <Badge variant="outline" className={`text-[10px] ${
              a.severity === 'severe' ? 'bg-red-50 text-red-700 border-red-200'
              : a.severity === 'moderate' ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-slate-50 text-slate-600'
            }`}>{a.severity}</Badge>
          </div>
        ))}
        {(!allergies || allergies.length === 0) && <p className="text-center py-8 text-sm text-muted-foreground">No known drug allergies (NKDA)</p>}
      </CardContent>
    </Card>
  );
}

function PreventiveContent({ patientId, rawItems }) {
  const [chipState, setChipState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGap, setSelectedGap] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [updateForm, setUpdateForm] = useState({ date: '', source: 'manual_entry', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showCompliant, setShowCompliant] = useState(false);

  const fetchChipState = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    api.post(`/patients/${patientId}/quality-chip-state`, {})
      .then(r => setChipState(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { fetchChipState(); }, [fetchChipState]);

  const openDrillDown = async (gap) => {
    setSelectedGap(gap);
    setEvidenceLoading(true);
    setUpdateForm({ date: new Date().toISOString().slice(0, 10), source: 'manual_entry', notes: '' });
    try {
      const res = await api.get(`/patients/${patientId}/preventive-care/evidence/${gap.measure_id}`);
      setEvidence(res.data);
    } catch { setEvidence(null); }
    finally { setEvidenceLoading(false); }
  };

  const handleUpdate = async (action) => {
    if (!selectedGap) return;
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/preventive-care/update`, {
        item_name: selectedGap.short_label || selectedGap.measure_id,
        action,
        completion_date: updateForm.date || undefined,
        source: updateForm.source,
        notes: updateForm.notes || undefined,
      });
      toast.success(`${selectedGap.short_label}: ${action}`);
      setSelectedGap(null);
      fetchChipState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const chips = chipState?.chips || [];
  const overdueChips   = chips.filter(c => c.status === 'overdue');
  const dueChips       = chips.filter(c => c.status === 'due');
  const pendingChips   = chips.filter(c => c.status === 'pending_confirmation');
  const stagedChips    = chips.filter(c => c.status === 'staged');
  const compliantChips = chips.filter(c => c.status === 'compliant');
  const openCount = overdueChips.length + dueChips.length + pendingChips.length;

  const expiryLabel = (chip) => {
    const e = chip.expiration;
    if (!e) return '';
    if (e.is_lifetime) return 'Lifetime';
    return e.expiration_label || '';
  };

  const statusDot = (status) => {
    if (status === 'overdue')              return 'bg-red-500';
    if (status === 'due')                  return 'bg-amber-400';
    if (status === 'pending_confirmation') return 'bg-violet-500';
    if (status === 'staged')               return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  const ACTION_MAP = {
    'qm_crc_screening':          [{ id: 'order', label: 'Order Cologuard' }, { id: 'declined', label: 'Declined' }],
    'qm_breast_screening':       [{ id: 'order', label: 'Refer Mammography' }, { id: 'declined', label: 'Declined' }],
    'qm_diabetes_a1c':           [{ id: 'order', label: 'Order A1c' }],
    'qm_flu_vaccine':            [{ id: 'completed', label: 'Mark Given' }, { id: 'declined', label: 'Declined' }],
    'qm_pneumococcal_vaccine':   [{ id: 'completed', label: 'Mark Given' }, { id: 'declined', label: 'Declined' }],
    'qm_fall_risk_screening':    [{ id: 'completed', label: 'Screen Done' }],
    'qm_cognitive_screening':    [{ id: 'completed', label: 'Screen Done' }],
    'qm_depression_screening':   [{ id: 'completed', label: 'PHQ-9 Done' }, { id: 'declined', label: 'Declined' }],
    'qm_osteoporosis_screening': [{ id: 'order', label: 'Order DXA' }, { id: 'declined', label: 'Declined' }],
    '_default': [{ id: 'completed', label: 'Mark Complete' }, { id: 'declined', label: 'Declined' }],
  };

  function GapRow({ chip, accent }) {
    const actions = ACTION_MAP[chip.measure_id] || ACTION_MAP['_default'];
    const expiry = expiryLabel(chip);
    const isSelected = selectedGap?.measure_id === chip.measure_id;
    return (
      <div className={`rounded-xl border p-3.5 space-y-2 transition-all ${
        isSelected ? 'border-primary/30 bg-primary/5' :
        accent === 'red' ? 'border-red-200 bg-red-50/20' :
        accent === 'violet' ? 'border-violet-200 bg-violet-50/20' : 'border-border bg-card'
      }`} data-testid={`preventive-gap-${chip.measure_id}`}>
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full shrink-0 ${statusDot(chip.status)}`} />
            <span className="text-sm font-semibold">{chip.short_label}</span>
            {chip.category && <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{chip.category}</span>}
          </div>
          <div className="text-right">
            {expiry && <p className={`text-[9px] ${chip.expiration?.is_critically_soon ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>{expiry}</p>}
            {chip.last_action_date && <p className="text-[9px] text-muted-foreground">Last: {new Date(chip.last_action_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 pl-4 flex-wrap">
          {actions.map(a => (
            <Button key={a.id} size="sm"
              variant={a.id === 'declined' ? 'outline' : 'default'}
              className={`h-7 text-[10px] px-3 gap-1 ${a.id === 'declined' ? 'border-slate-300 text-slate-600 hover:bg-slate-50' : ''}`}
              onClick={() => { setSelectedGap(chip); if (a.id === 'declined') handleUpdate('declined'); else openDrillDown(chip); }}
              data-testid={`gap-action-${chip.measure_id}-${a.id}`}>
              {a.id === 'declined' ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
              {a.label}
            </Button>
          ))}
          <button onClick={() => openDrillDown(chip)}
            className="text-[9px] text-primary hover:text-primary/80 ml-auto" data-testid={`gap-drill-${chip.measure_id}`}>
            Evidence →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — chip strip replaces old 4-box KPI grid */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <ShieldCheck className="w-4 h-4 text-primary" /> Quality Measures
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">ACO / MIPS · {chips.length} tracked · {openCount} open</p>
        </div>
        <QualityChipStrip patientId={patientId} context="chart" density="compact" className="gap-1.5" />
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted/40 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="space-y-4">
          {overdueChips.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Overdue
              </p>
              {overdueChips.map(c => <GapRow key={c.measure_id} chip={c} accent="red" />)}
            </div>
          )}
          {pendingChips.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600 flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5" /> Needs Confirmation
              </p>
              {pendingChips.map(c => <GapRow key={c.measure_id} chip={c} accent="violet" />)}
            </div>
          )}
          {dueChips.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Due This Period
              </p>
              {dueChips.map(c => <GapRow key={c.measure_id} chip={c} accent="amber" />)}
            </div>
          )}
          {stagedChips.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Staged / Pending Result
              </p>
              {stagedChips.map(c => (
                <div key={c.measure_id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-blue-200 bg-blue-50/20">
                  <Clock className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  <span className="text-sm font-medium flex-1">{c.short_label}</span>
                  <span className="text-[10px] text-blue-600">Awaiting result</span>
                </div>
              ))}
            </div>
          )}
          {openCount === 0 && chips.length > 0 && (
            <div className="text-center py-8 rounded-xl border border-emerald-200 bg-emerald-50/30">
              <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-700">All quality measures up to date</p>
            </div>
          )}
          {compliantChips.length > 0 && (
            <div>
              <button onClick={() => setShowCompliant(c => !c)}
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground py-1">
                <Check className="w-3 h-3 text-emerald-500" />
                {compliantChips.length} compliant {showCompliant ? '▲' : '▼'}
              </button>
              {showCompliant && (
                <div className="mt-1.5 space-y-1.5">
                  {compliantChips.map(c => (
                    <div key={c.measure_id} className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50/10">
                      <div className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span className="text-sm font-medium">{c.short_label}</span>
                        {c.category && <span className="text-[9px] text-muted-foreground">{c.category}</span>}
                      </div>
                      <div className="text-right">
                        {expiryLabel(c) && <p className={`text-[9px] ${c.expiration?.is_critically_soon ? 'text-amber-600 font-semibold' : 'text-emerald-600'}`}>{expiryLabel(c)}</p>}
                        {c.last_action_date && <p className="text-[9px] text-muted-foreground">{new Date(c.last_action_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preventive Care History */}
      {rawItems && rawItems.length > 0 && (
        <div className="space-y-1.5 pt-2 border-t">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Preventive Care Record</p>
          {rawItems.map((item, i) => {
            const isDue = item.status === 'due_soon' || item.status === 'due';
            const isOk = item.status === 'current' || item.status === 'compliant';
            return (
              <div key={i} className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${isDue ? 'border-amber-200 bg-amber-50/10' : isOk ? 'border-emerald-200 bg-emerald-50/10' : 'border-border bg-card'}`}>
                <div>
                  <p className="text-sm font-medium">{item.item_name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Last: {item.last_completed || 'Never'} · Next: {item.next_due || 'N/A'}</p>
                </div>
                <Badge variant="outline" className={`text-[9px] ${isDue ? 'bg-amber-50 text-amber-700 border-amber-300' : isOk ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600'}`}>
                  {item.status?.replace('_', ' ')}
                </Badge>
              </div>
            );
          })}
        </div>
      )}

      {/* Drill-down panel */}
      {selectedGap && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="py-3 px-4 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" /> {selectedGap.short_label}
              </CardTitle>
              <button onClick={() => setSelectedGap(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {evidenceLoading ? <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" /></div> : (
              <>
                {evidence?.records?.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Prior Evidence</p>
                    {evidence.records.slice(0, 3).map((r, i) => (
                      <div key={i} className="text-xs text-muted-foreground flex justify-between border-b border-dashed pb-1 last:border-0">
                        <span>{r.source || 'Chart record'}</span><span>{r.date || r.completion_date}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Record Update</p>
                  <input type="date" value={updateForm.date} onChange={e => setUpdateForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full text-xs border rounded-lg px-3 py-2 bg-background" />
                  <select value={updateForm.source} onChange={e => setUpdateForm(f => ({ ...f, source: e.target.value }))}
                    className="w-full text-xs border rounded-lg px-3 py-2 bg-background">
                    <option value="manual_entry">Manual Entry</option>
                    <option value="patient_reported">Patient Reported</option>
                    <option value="external_record">External Record</option>
                    <option value="lab_result">Lab Result</option>
                  </select>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="flex-1 text-xs gap-1" onClick={() => handleUpdate('completed')} disabled={saving || !updateForm.date}>
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Mark Complete
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleUpdate('declined')} disabled={saving}>
                      Patient Declined
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DeferredContent({ items, onResolve }) {
  const open = items.filter(d => d.resolution_status === 'open');
  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-center py-12"><History className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No deferred updates</p></div>
      ) : (
        <>
          {open.map(item => (
            <Card key={item.id} className="border-amber-200/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.description || item.condition_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">{item.update_type?.replace(/_/g, ' ')}</Badge>
                      <ConfidenceBadge score={item.confidence} size="xs" />
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button size="sm" className="h-7 text-xs gap-1" onClick={() => onResolve(item.id, 'approve')}><Check className="w-3 h-3" /> Apply</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => onResolve(item.id, 'reject')}><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

function TasksContent({ encounters }) {
  const tasks = encounters.filter(e => e.encounter_outputs?.visit_summary?.follow_up_items?.length > 0);
  return (
    <div className="space-y-3">
      {tasks.length === 0 ? (
        <div className="text-center py-12"><ClipboardList className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No follow-up tasks</p></div>
      ) : (
        tasks.map((enc, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2 tabular-nums">{enc.date?.split('T')[0]} - {enc.provider_name}</p>
              {enc.encounter_outputs.visit_summary.follow_up_items.map((f, j) => (
                <div key={j} className="flex items-center gap-2 py-1">
                  <ClipboardList className="w-3 h-3 text-muted-foreground" />
                  <span className="text-sm">{f.item}</span>
                  {f.interval && <Badge variant="outline" className="text-[10px]">{f.interval}</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function CareTeamContent({ careTeam }) {
  return (
    <Card>
      <CardContent className="p-0">
        {(careTeam || []).map((ct, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 border-b last:border-0">
            <div className="w-8 h-8 rounded-full bg-sky-50 flex items-center justify-center text-xs font-semibold text-sky-600">{ct.provider_name?.charAt(0)}</div>
            <div>
              <p className="text-sm font-medium">{ct.provider_name}</p>
              <p className="text-xs text-muted-foreground">{ct.role} {ct.facility && `- ${ct.facility}`}</p>
            </div>
          </div>
        ))}
        {(!careTeam || careTeam.length === 0) && <p className="text-center py-8 text-sm text-muted-foreground">No care team members</p>}
      </CardContent>
    </Card>
  );
}


function DocumentsContent({ patientId }) {
  const [inboxDocs, setInboxDocs] = useState([]);
  const [chartDocs, setChartDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    Promise.all([
      api.get(`/documents/patient/${patientId}`).catch(() => ({ data: { documents: [] } })),
      api.get(`/patients/${patientId}/documents`).catch(() => ({ data: { documents: [] } })),
    ]).then(([inboxRes, chartRes]) => {
      setInboxDocs(inboxRes.data.documents || []);
      setChartDocs(chartRes.data.documents || []);
    }).finally(() => setLoading(false));
  }, [patientId]);

  // Unified folder structure — clinical chart docs + inbox docs
  const FOLDERS = [
    { key: 'screeners', name: 'Screeners / Assessments', icon: Brain, color: '#7C3AED', desc: 'PHQ-9, MMSE, fall risk, screening tools' },
    { key: 'med_admin', name: 'Medication Administration', icon: Pill, color: '#059669', desc: 'In-clinic medication records' },
    { key: 'letters', name: 'Patient Letters / Notes', icon: Send, color: '#0D9488', desc: 'Work notes, school notes, patient letters' },
    { key: 'clinical_forms', name: 'Clinical Forms', icon: ClipboardList, color: '#2563EB', desc: 'Vaccines, procedures, clinical documentation' },
    { key: 'lab_result', name: 'Lab Results', icon: Beaker, color: '#D97706', desc: 'Blood work, urinalysis, pathology' },
    { key: 'imaging_report', name: 'Imaging', icon: FileText, color: '#6366F1', desc: 'X-rays, CT, MRI, ultrasound reports' },
    { key: 'referral', name: 'Referrals', icon: Send, color: '#0EA5E9', desc: 'Outgoing and incoming referrals' },
    { key: 'consult_note', name: 'Consult Reports', icon: Users, color: '#059669', desc: 'Specialist consultation notes' },
    { key: 'hospital_record', name: 'Hospital Records', icon: FileText, color: '#DC2626', desc: 'Discharge summaries, ER visits, admissions' },
    { key: 'insurance_prior_auth', name: 'Insurance / Prior Auth', icon: Shield, color: '#F59E0B', desc: 'Prior authorizations, EOBs, insurance docs' },
    { key: 'medication_related', name: 'Medication Records', icon: Pill, color: '#EC4899', desc: 'Prescription records, pharmacy communications' },
    { key: 'administrative_form', name: 'Administrative Forms', icon: ClipboardList, color: '#6B7280', desc: 'HIPAA, consent, registration forms' },
    { key: 'miscellaneous', name: 'Other Documents', icon: FolderOpen, color: '#6B7280', desc: 'Uncategorized and other files' },
  ];

  // Merge all docs into folders
  const allDocs = [];

  // Chart documents (from nurse tasks)
  chartDocs.forEach(d => {
    allDocs.push({
      ...d, _source: 'chart',
      _folder: d.folder || 'clinical_forms',
      _title: d.title,
      _date: d.created_at,
      _tags: d.tags || [],
      _type: d.doc_type,
      _by: d.created_by_role ? `${d.created_by_role}` : '',
      _summary: d.summary_text,
    });
  });

  // Inbox documents
  inboxDocs.forEach(d => {
    const cat = d.selected_category || d.suggested_category || 'miscellaneous';
    allDocs.push({
      ...d, _source: 'inbox',
      _folder: cat,
      _title: d.title || d.filename || 'Untitled',
      _date: d.filed_at || d.signed_at || d.uploaded_at,
      _tags: d.review_status === 'signed' ? ['signed'] : [],
      _type: cat,
      _by: d.signed_by_name || '',
      _summary: d.sign_off?.physician_note || '',
    });
  });

  // Apply search filter
  const searchLower = search.toLowerCase().trim();
  const filtered = searchLower ? allDocs.filter(d =>
    (d._title || '').toLowerCase().includes(searchLower) ||
    (d._tags || []).some(t => t.toLowerCase().includes(searchLower)) ||
    (d._type || '').toLowerCase().includes(searchLower) ||
    (d._summary || '').toLowerCase().includes(searchLower) ||
    (d._folder || '').toLowerCase().includes(searchLower)
  ) : allDocs;

  // Build folder counts
  const folders = FOLDERS.map(f => ({
    ...f,
    docs: filtered.filter(d => d._folder === f.key),
    count: filtered.filter(d => d._folder === f.key).length,
  }));

  if (loading) return <div className="text-center py-8 text-sm text-muted-foreground">Loading documents...</div>;

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search documents by title, tag, or type..."
          className="pl-8 h-9 text-sm"
          data-testid="doc-search-input"
        />
        {search && (
          <span className="absolute right-2.5 top-2 text-[10px] text-muted-foreground">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span>{allDocs.length} total documents</span>
        <span>{chartDocs.length} clinical</span>
        <span>{inboxDocs.length} inbox</span>
      </div>

      {/* Folder list */}
      {folders.map(folder => {
        const Icon = folder.icon;
        const isOpen = expanded === folder.key;
        if (folder.count === 0 && folder.key === 'miscellaneous' && !search) return null;
        return (
          <div key={folder.key}>
            <Card className={`cursor-pointer transition-all ${folder.count > 0 ? 'hover:shadow-md hover:border-slate-300' : 'opacity-40'}`}
              data-testid={`doc-folder-${folder.key}`}
              onClick={() => (folder.count > 0 || folder.key === 'lab_result') && setExpanded(isOpen ? null : folder.key)}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${folder.color}12` }}>
                  <Icon className="w-4 h-4" style={{ color: folder.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{folder.name}</p>
                  <p className="text-[10px] text-muted-foreground">{folder.desc}</p>
                </div>
                <Badge variant={folder.count > 0 ? "default" : "outline"} className="text-[10px] tabular-nums shrink-0">{folder.count}</Badge>
                {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              </CardContent>
            </Card>
            {isOpen && folder.key === 'lab_result' && (
              <div className="ml-4 mt-2 pb-2">
                <LabDashboard patientId={patientId} />
              </div>
            )}
            {isOpen && folder.key !== 'lab_result' && folder.docs.length > 0 && (
              <div className="ml-4 mt-1 space-y-1.5 border-l-2 pl-3 pb-2" style={{ borderColor: `${folder.color}30` }}>
                {folder.docs.map(doc => (
                  <DocItemCard key={doc.id} doc={doc} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state */}
      {allDocs.length === 0 && (
        <div className="text-center py-12">
          <FolderOpen className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
          <p className="text-sm text-muted-foreground">No documents on file</p>
        </div>
      )}
      {search && filtered.length === 0 && allDocs.length > 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">No documents match "{search}"</p>
        </div>
      )}
    </div>
  );
}

function DocItemCard({ doc }) {
  const [expanded, setExpanded] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [convs, setConvs] = useState(null);

  const handleViewPdf = async () => {
    if (pdfUrl) { setViewingPdf(!viewingPdf); return; }
    try {
      const token = localStorage.getItem('prattos_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/pdf') || contentType.includes('image/')) {
          const blob = await res.blob();
          setPdfUrl(URL.createObjectURL(blob));
          setViewingPdf(true);
        } else {
          toast.info('No original PDF on file — document content is shown above');
        }
      } else { toast.error('PDF not available'); }
    } catch { toast.error('Failed to load PDF'); }
  };

  const loadConversations = async () => {
    if (convs !== null) return;
    try {
      const patientId = doc.patient_id || doc.selected_patient_id;
      if (!patientId) { setConvs([]); return; }
      const res = await api.get(`/conversations?patient_id=${patientId}`);
      const all = res.data.conversations || res.data.items || [];
      // Match by linked artifact ID or by title containing the document title
      const docId = doc.id || doc._id;
      const docTitle = (doc._title || doc.title || '').substring(0, 25);
      const linked = all.filter(c => {
        // Check linked_artifacts array
        if ((c.linked_artifacts || []).some(a => a.artifact_id === docId)) return true;
        // Check title match (conversations created from doc sign-off include the doc title)
        if (docTitle && (c.title || '').includes(docTitle)) return true;
        return false;
      });
      // Load messages for each linked conversation
      const enriched = [];
      for (const c of linked) {
        try {
          const cid = c.id || c._id;
          const msgRes = await api.get(`/conversations/${cid}`);
          const convData = msgRes.data.conversation || msgRes.data;
          enriched.push({ ...c, ...convData });
        } catch { enriched.push(c); }
      }
      setConvs(enriched);
    } catch { setConvs([]); }
  };

  const handleExpand = () => {
    if (!expanded) loadConversations();
    setExpanded(!expanded);
  };

  const signOff = doc.sign_off || {};
  const ocrText = doc.ocr_text || doc.ocr_preview || '';
  const hasOcr = ocrText.length > 0;

  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden transition-all hover:shadow-md"
      data-testid={`doc-item-${doc.id}`}>
      {/* Header — always visible */}
      <button onClick={handleExpand} className="w-full text-left px-3.5 py-3 flex items-center gap-3">
        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{doc._title}</p>
          {doc._summary && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{doc._summary}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {doc._tags?.includes('signed') && (
            <Badge variant="outline" className="text-[8px] border-emerald-200 text-emerald-600">signed</Badge>
          )}
          {doc._by && <span className="text-[10px] text-blue-500">{doc._by}</span>}
          <span className="text-[10px] text-slate-400">{doc._date ? new Date(doc._date).toLocaleDateString() : ''}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3.5 pb-3.5 border-t border-slate-100 space-y-3">
          {/* Sign-off info + View PDF row */}
          <div className="mt-2 flex items-center gap-2">
            {signOff.signed_at && (
              <div className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="flex-1 text-[11px]">
                  <span className="font-semibold text-emerald-700">Signed off by {signOff.signed_by_name || 'Provider'}</span>
                  <span className="text-emerald-500 ml-2">
                    {new Date(signOff.signed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            )}
            {(doc.file_path || doc.signed_file_path || doc._source === 'inbox') && (
              <Button size="sm" variant="outline" className="text-[10px] gap-1 h-8 shrink-0" onClick={handleViewPdf}>
                <Eye className="w-3 h-3" /> {viewingPdf ? 'Hide PDF' : 'View PDF'}
              </Button>
            )}
          </div>

          {/* PDF Viewer */}
          {viewingPdf && pdfUrl && (
            <div className="rounded-lg border border-slate-200 overflow-hidden" style={{ height: '350px' }}>
              <iframe src={pdfUrl} className="w-full h-full" title="Document PDF" />
            </div>
          )}

          {/* Physician note */}
          {signOff.physician_note && (
            <div className="rounded-lg bg-blue-50/50 border border-blue-100 px-3 py-2">
              <p className="text-[9px] font-semibold text-blue-500 uppercase mb-0.5">Physician Note</p>
              <p className="text-xs text-slate-700">{signOff.physician_note}</p>
            </div>
          )}

          {/* OCR / AI Summary — compact with scroll */}
          {hasOcr && (
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 max-h-28 overflow-y-auto">
              <p className="text-[9px] font-semibold text-slate-400 uppercase mb-1">Document Content</p>
              <p className="text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed font-mono">{ocrText.substring(0, 500)}{ocrText.length > 500 ? '...' : ''}</p>
            </div>
          )}

          {/* Linked conversations with message threads */}
          {convs && convs.length > 0 && (
            <div className="space-y-2">
              <p className="text-[9px] font-semibold text-slate-400 uppercase">Communications</p>
              {convs.map(c => {
                const msgs = c.messages || [];
                return (
                  <div key={c.id || c._id} className="rounded-lg border border-blue-100 overflow-hidden">
                    <div className="px-3 py-2 bg-blue-50/30 flex items-center gap-2">
                      <History className="w-3 h-3 text-blue-400" />
                      <span className="text-[11px] font-medium text-slate-700 flex-1 truncate">{c.title}</span>
                      <Badge variant="outline" className={`text-[8px] ${c.status === 'resolved' ? 'border-emerald-200 text-emerald-600' : 'border-amber-200 text-amber-600'}`}>
                        {c.status}
                      </Badge>
                    </div>
                    {/* Message thread */}
                    {msgs.length > 0 && (
                      <div className="px-3 py-2 space-y-1.5 max-h-48 overflow-y-auto">
                        {msgs.map((msg, i) => (
                          <div key={msg.id || i} className={`rounded-lg px-2.5 py-1.5 text-[10px] ${
                            msg.sender_role === 'provider' ? 'bg-slate-100 ml-6' : 'bg-blue-50 mr-6'
                          }`}>
                            <div className="flex items-center gap-1 mb-0.5">
                              <span className="font-semibold text-[9px]">{msg.sender_name}</span>
                              <span className="text-[8px] text-slate-400">
                                {msg.timestamp ? new Date(msg.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                            <p className="text-slate-700 leading-relaxed">{msg.body}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Action request */}
                    {c.action_request && (
                      <div className="px-3 py-1.5 border-t border-blue-100 bg-amber-50/30">
                        <span className="text-[9px] font-semibold text-amber-700">Action: </span>
                        <span className="text-[10px] text-amber-600">{c.action_request.text}</span>
                        {c.action_request.completion_status && (
                          <span className="text-[9px] text-emerald-600 ml-2">({c.action_request.completion_status.replace(/_/g, ' ')})</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function LabResultsContent({ patientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) return;
    api.get(`/documents/patient/${patientId}`).then(res => {
      const labs = (res.data.documents || []).filter(d =>
        (d.selected_category || d.suggested_category) === 'lab_result'
      );
      setDocs(labs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="text-center py-8 text-sm text-muted-foreground">Loading lab results...</div>;

  if (docs.length === 0) {
    return (
      <div className="text-center py-12">
        <Beaker className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
        <p className="text-sm text-muted-foreground">No lab results on file</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {docs.map(doc => (
        <Card key={doc.id} className="shadow-none" data-testid={`lab-doc-${doc.id}`}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Beaker className="w-4 h-4 text-violet-600 shrink-0" />
              <span className="text-sm font-medium flex-1 min-w-0 truncate">{doc.filename}</span>
              {doc.page_count > 1 && <Badge variant="outline" className="text-[9px] shrink-0">{doc.page_count} pages</Badge>}
              <span className="text-[10px] text-muted-foreground">{doc.filed_at ? new Date(doc.filed_at).toLocaleDateString() : ''}</span>
            </div>
            {doc.cover_sheet?.highlights?.length > 0 && (
              <div className="mt-1.5 pl-6 space-y-0.5">
                {doc.cover_sheet.highlights.filter(h => h.type === 'lab').slice(0, 5).map((h, i) => (
                  <p key={i} className="text-[10px] text-muted-foreground">
                    <span className="font-medium">{h.label}:</span> {h.value} {h.reference && <span className="text-[9px]">({h.reference})</span>}
                  </p>
                ))}
              </div>
            )}
            {doc.cover_sheet?.actions?.length > 0 && (
              <div className="mt-1 pl-6">
                {doc.cover_sheet.actions.slice(0, 2).map((a, i) => (
                  <p key={i} className="text-[10px] text-amber-600">{a.signal}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PlaceholderContent({ title, icon: Icon }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
      <p className="text-sm text-muted-foreground">{title} will be available in a future update</p>
    </div>
  );
}

function FamilyHistoryEditorSection({ patientId, patient }) {
  const { FamilyHistoryEditor } = require('../components/patient_chart/FamilyHistoryEditor');
  return <FamilyHistoryEditor patientId={patientId} initialHistory={patient.family_history || []} />;
}

function SocialHistorySection({ patientId, patient }) {
  const { SocialHistoryPanel } = require('../components/patient_chart/SocialHistoryPanel');
  return <SocialHistoryPanel patientId={patientId} initialHistory={patient.social_history || []} />;
}



// ============================================================
// MAIN COMPONENT — CLINICAL iPAD LAYOUT
// ============================================================

export default function PatientChart() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const returnConvId = searchParams.get('convId');
  const SECTION_ALIASES = { medications: 'meds', labs: 'labs', documents: 'documents', overview: 'overview' };
  const initialSection = SECTION_ALIASES[searchParams.get('section')] || searchParams.get('section');
  const [patient, setPatient] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [deferredUpdates, setDeferredUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(initialSection || 'overview');
  const [allergyDialog, setAllergyDialog] = useState(false);
  const [allergyForm, setAllergyForm] = useState({ name: '', severity: 'moderate', reaction: '' });
  const [chartDrawerChip, setChartDrawerChip] = useState(null);
  const [qualityHeader, setQualityHeader] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [patRes, encRes, defRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/encounters/patient/${patientId}`),
        api.get(`/deferred-updates/${patientId}`).catch(() => ({ data: { deferred_updates: [] } })),
      ]);
      setPatient(patRes.data.patient);
      setEncounters(encRes.data.encounters || []);
      setDeferredUpdates(defRes.data.deferred_updates || []);
      // Fetch quality status for header pill
      try {
        const qRes = await api.get(`/patients/${patientId}/quality-overview`);
        setQualityHeader(qRes.data?.header);
      } catch {}
    } catch (err) {
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resolveDeferredItem = async (itemId, action) => {
    try {
      await api.post(`/deferred-updates/${itemId}/resolve`, { action });
      toast.success(action === 'approve' ? 'Applied to chart' : 'Dismissed');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return (
    <div className="flex h-full animate-pulse">
      <div className="w-56 border-r p-3 space-y-2"><div className="h-6 bg-muted rounded" />{[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-muted rounded" />)}</div>
      <div className="flex-1 p-6 space-y-4"><div className="h-12 bg-muted rounded-xl" /><div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-muted rounded-xl" />)}</div></div>
    </div>
  );
  if (!patient) return <div className="p-6 text-center text-muted-foreground">Patient not found</div>;

  const openDeferred = deferredUpdates.filter(d => d.resolution_status === 'open').length;
  const allergies = patient.allergies || [];
  const currentSection = SECTIONS.find(s => s.id === activeSection);

  // Group sections
  const groups = {};
  SECTIONS.forEach(s => {
    if (!groups[s.group]) groups[s.group] = [];
    groups[s.group].push(s);
  });

  return (
    <div className="flex flex-col h-full" data-testid="patient-chart-ipad">
      {/* Return-to banner */}
      {returnTo && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 shrink-0">
          <button onClick={() => {
              const url = returnConvId ? `/my-day?openConv=${returnConvId}` : `/${returnTo}`;
              navigate(url);
            }}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            data-testid="return-to-btn">
            <ChevronLeft className="w-3.5 h-3.5" /> Back to My Day
          </button>
          <span className="text-[10px] text-blue-400">Viewing {patient?.name?.first} {patient?.name?.last}'s chart</span>
        </div>
      )}
      {/* Frosted Glass Patient Header */}
      <div className="px-5 py-3 border-b bg-white/80 backdrop-blur-xl flex items-center gap-4 shrink-0 sticky top-0 z-10">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
          {patient.name?.first?.[0]}{patient.name?.last?.[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              {patient.name?.first} {patient.name?.last}
            </h1>
            <Badge variant="outline" className="text-[10px] font-mono tabular-nums">{patient.mrn}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums">{patient.dob}</span>
            <span>{patient.sex}</span>
            <span>{patient.phone}</span>
            {patient.insurance?.payer_name && (
              <span>{patient.insurance.payer_name} ({patient.insurance.payer_class})</span>
            )}
          </div>
        </div>
        {/* Quality Chip Strip — replaces simple status pill */}
        <div className="flex items-center gap-2 flex-wrap">
          <QualityChipStrip
            patientId={patientId}
            context="chart"
            density="compact"
            onChipClick={(chip) => setChartDrawerChip(chip)}
            className="gap-1"
          />
        </div>
        {/* Allergy Badge — Clickable to edit */}
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border shrink-0 transition-colors hover:shadow-sm
            ${allergies.length > 0 ? 'bg-amber-50 border-amber-200 hover:bg-amber-100' : 'bg-muted/50 border-border hover:bg-accent'}`}
          onClick={() => setAllergyDialog(true)}
          data-testid="allergy-badge-button"
        >
          <AlertCircle className={`w-3.5 h-3.5 ${allergies.length > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
          <span className={`text-xs font-medium ${allergies.length > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
            {allergies.length > 0 ? allergies.map(a => a.name).join(', ') : 'NKDA'}
          </span>
        </button>
      </div>

      {/* Quality chip drawer */}
      {chartDrawerChip && (
        <QualityChipStripDrawer
          chip={chartDrawerChip}
          encounterId={null}
          onClose={() => setChartDrawerChip(null)}
          onActionTaken={() => setChartDrawerChip(null)}
        />
      )}

      {/* Split View: Sidebar + Content */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Chart Sidebar */}
        <ResizablePanel defaultSize={18} minSize={14} maxSize={28}>
          <ScrollArea className="h-full">
            <div className="p-2 pt-3">
              {Object.entries(groups).map(([groupName, items]) => (
                <div key={groupName} className="mb-3">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">{groupName}</p>
                  {items.map(section => {
                    const isActive = activeSection === section.id;
                    const Icon = section.icon;
                    const badge = section.id === 'deferred' && openDeferred > 0 ? openDeferred : null;
                    return (
                      <button
                        key={section.id}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition-colors text-sm
                          ${isActive
                            ? 'bg-primary/[0.08] text-primary font-medium'
                            : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        onClick={() => setActiveSection(section.id)}
                        data-testid={`chart-section-${section.id}`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-primary' : 'text-slate-400'}`} />
                        <span className="flex-1 truncate">{section.label}</span>
                        {badge && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 bg-amber-50 text-amber-700 border-amber-200">{badge}</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </ScrollArea>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Main Content */}
        <ResizablePanel defaultSize={82}>
          <ScrollArea className="h-full">
            <div className="p-6">
              {/* Section Title */}
              <div className="flex items-center gap-2 mb-4">
                {currentSection && <currentSection.icon className="w-5 h-5 text-primary" />}
                <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
                  {currentSection?.label || 'Overview'}
                </h2>
              </div>

              {/* Content Router */}
              {activeSection === 'overview' && <OverviewContent patient={patient} encounters={encounters} onNavigate={setActiveSection} />}
              {activeSection === 'problems' && <ProblemsContent problemList={patient.problem_list} patientId={patientId} />}
              {activeSection === 'notes' && <NotesContent encounters={encounters} navigate={navigate} patientId={patientId} />}
              {activeSection === 'meds' && <MedsContent medications={patient.medications} patientId={patientId} />}
              {activeSection === 'preventive' && <PreventiveContent patientId={patientId} rawItems={patient.preventive_care} />}
              {activeSection === 'deferred' && <DeferredContent items={deferredUpdates} onResolve={resolveDeferredItem} />}
              {activeSection === 'tasks' && <TasksContent encounters={encounters} />}
              {activeSection === 'care_team' && <CareTeamContent careTeam={patient.care_team} />}
              {activeSection === 'labs' && <LabDashboard patientId={patientId} />}
              {activeSection === 'family' && <FamilyHistoryEditorSection patientId={patientId} patient={patient} />}
              {activeSection === 'social' && <SocialHistorySection patientId={patientId} patient={patient} />}
              {activeSection === 'referrals' && <PlaceholderContent title="Referrals" icon={Send} />}
              {activeSection === 'documents' && <DocumentsContent patientId={patientId} />}
            </div>
          </ScrollArea>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Allergy Edit Dialog */}
      <Dialog open={allergyDialog} onOpenChange={setAllergyDialog}>
        <DialogContent className="sm:max-w-md" data-testid="allergy-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
              <AlertCircle className="w-4 h-4 text-amber-500 inline mr-2" />
              Allergies
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Current allergies */}
            {allergies.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">No known drug allergies (NKDA)</p>
            ) : (
              allergies.map((a, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-amber-50/50 border-amber-200/50">
                  <div>
                    <span className="text-sm font-medium">{a.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">({a.severity})</span>
                    {a.reaction && <span className="text-xs text-muted-foreground ml-1">— {a.reaction}</span>}
                  </div>
                </div>
              ))
            )}

            {/* Add new allergy form */}
            <div className="border-t pt-3">
              <p className="text-xs font-medium mb-2">Add Allergy</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Input className="h-8 text-xs" placeholder="Allergy name"
                    value={allergyForm.name}
                    onChange={e => setAllergyForm(f => ({ ...f, name: e.target.value }))}
                    data-testid="allergy-name-input" />
                </div>
                <div>
                  <Select value={allergyForm.severity} onValueChange={v => setAllergyForm(f => ({ ...f, severity: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="allergy-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild" className="text-xs">Mild</SelectItem>
                      <SelectItem value="moderate" className="text-xs">Moderate</SelectItem>
                      <SelectItem value="severe" className="text-xs">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Input className="h-8 text-xs" placeholder="Reaction"
                    value={allergyForm.reaction}
                    onChange={e => setAllergyForm(f => ({ ...f, reaction: e.target.value }))}
                    data-testid="allergy-reaction-input" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAllergyDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
